import React from 'react';
import { Button, ButtonProps } from '@mui/material';
import { alpha } from '@mui/material/styles';

export type AppButtonTone = 'primary' | 'secondary' | 'danger' | 'ghost';

export type AppButtonProps = Omit<ButtonProps, 'color' | 'variant'> & {
  tone?: AppButtonTone;
};

export const AppButton: React.FC<AppButtonProps> = ({
  tone = 'primary',
  sx,
  ...props
}) => {
  return (
    <Button
      {...props}
      variant={tone === 'secondary' ? 'outlined' : tone === 'ghost' ? 'text' : 'contained'}
      sx={[
        {
          textTransform: 'none',
          fontWeight: 600,
          letterSpacing: '0.01em',
          borderRadius: 2,
          minWidth: 96,
          px: 1.8,
          backdropFilter: tone === 'ghost' ? undefined : 'blur(3px)',
          transition:
            'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease',
          '&:active': {
            transform: 'translateY(0.5px)'
          }
        },
        tone === 'secondary'
          ? {
              borderColor: (theme) => theme.designSystem.button.outlinedBorder,
              color: (theme) => theme.designSystem.button.outlinedText,
              background: (theme) =>
                `linear-gradient(160deg, ${theme.designSystem.button.outlinedBackground}, ${theme.designSystem.button.outlinedBackgroundHover})`,
              boxShadow: (theme) =>
                `0 0 0 1px ${alpha(theme.designSystem.button.outlinedBorder, 0.55)}`,
              '&:hover': {
                borderColor: (theme) =>
                  theme.designSystem.button.outlinedBorderHover,
                background: (theme) =>
                  `linear-gradient(160deg, ${theme.designSystem.button.outlinedBackgroundHover}, ${theme.designSystem.button.outlinedBackground})`,
                boxShadow: (theme) =>
                  `0 0 0 1px ${alpha(theme.designSystem.button.outlinedBorderHover, 0.45)}`
              },
              '&.Mui-focusVisible': {
                boxShadow: (theme) =>
                  `0 0 0 2px ${alpha(theme.designSystem.button.outlinedBorderHover, 0.35)}`
              }
            }
          : tone === 'danger'
            ? {
                border: (theme) =>
                  `1px solid ${theme.designSystem.button.dangerBorder}`,
                color: (theme) => theme.designSystem.button.dangerText,
                background: (theme) => theme.designSystem.button.dangerGradient,
                '&:hover': {
                  borderColor: (theme) =>
                    theme.designSystem.button.dangerBorderHover,
                  background: (theme) =>
                    theme.designSystem.button.dangerGradientHover
                },
                '&.Mui-focusVisible': {
                  boxShadow: (theme) =>
                    `0 0 0 2px ${alpha(theme.designSystem.button.dangerBorderHover, 0.36)}`
                }
              }
            : tone === 'ghost'
              ? {
                  color: (theme) => theme.designSystem.button.ghostText,
                  backgroundColor: (theme) =>
                    theme.designSystem.button.ghostBackground,
                  border: (theme) =>
                    `1px solid ${theme.designSystem.button.ghostBorder}`,
                  '&:hover': {
                    backgroundColor: (theme) =>
                      theme.designSystem.button.ghostBackgroundHover
                  },
                  '&.Mui-focusVisible': {
                    boxShadow: (theme) =>
                      `0 0 0 2px ${alpha(theme.designSystem.button.ghostBorder, 0.32)}`
                  }
                }
              : {
                  border: (theme) =>
                    `1px solid ${theme.designSystem.button.primaryBorder}`,
                  color: (theme) => theme.designSystem.button.primaryText,
                  background: (theme) => theme.designSystem.button.primaryGradient,
                  '&:hover': {
                    borderColor: (theme) =>
                      theme.designSystem.button.primaryBorderHover,
                    background: (theme) =>
                      theme.designSystem.button.primaryGradientHover
                  },
                  '&.Mui-focusVisible': {
                    boxShadow: (theme) =>
                      `0 0 0 2px ${alpha(theme.designSystem.button.primaryBorderHover, 0.36)}`
                  }
                },
        {
          '&.Mui-disabled': {
            color: (theme) => theme.designSystem.button.disabledText,
            border: (theme) =>
              `1px solid ${theme.designSystem.button.disabledBorder}`,
            borderColor: (theme) => theme.designSystem.button.disabledBorder,
            background: (theme) => theme.designSystem.button.disabledBackground,
            backgroundColor: (theme) =>
              theme.designSystem.button.disabledBackground,
            boxShadow: 'none',
            opacity: 1,
            transform: 'none'
          }
        },
        ...(sx ? (Array.isArray(sx) ? sx : [sx]) : [])
      ]}
    />
  );
};
